/*
 * Copyright (c) Castle Point Learning Systems Corporation, 2013-2023, all rights reserved.
 * NOTICE: Dissemination of the information and/or materials contained in this file is strictly
 * forbidden unless prior written permission is obtained from Castle Point Learning Systems Corporation.
 * See LICENSE.txt (part of this package) for the full notice.
 */

import {Dispatch, SetStateAction, useCallback, useEffect, useState} from "react";

function renewValue<T>(key: string, defaultValue: T): T {
    const item = sessionStorage.getItem(key)
    if(item){
        try {
            return JSON.parse(item)
        } catch (e){}
    }
    let obj
    if(defaultValue instanceof Function){
        obj = defaultValue()
    } else{
        obj = defaultValue
    }
    sessionStorage.setItem(key, JSON.stringify(obj))
    return obj
}

export function useSessionState<T>(key: string, defaultValue: T): [T, Dispatch<SetStateAction<T>>] {
    const [value, setValue] = useState<T>(() => defaultValue)

    useEffect(() => {
        setValue(renewValue(key, defaultValue))
    }, [key]);

    const setExternal: Dispatch<SetStateAction<any>> = useCallback((action: SetStateAction<T>) => {
        if (action instanceof Function) {
            setValue(prevState => {
                const value = action(prevState)
                sessionStorage.setItem(key, JSON.stringify(value))
                return value
            })
        } else {
            sessionStorage.setItem(key, JSON.stringify(action))
            setValue(action)
        }
    }, [setValue, key])

    return [value, setExternal]
}